import type { WorkshopTemplate } from "./types";

/**
 * Built-in templates that double as both the "starter" gallery and
 * the seed for the first-run examples. Keep ``build`` deterministic
 * given the same input so the demo data is reproducible.
 */

function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Deterministic-ish PRNG so the demo numbers don't jitter wildly. */
function pseudo(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export const INTRO_TEMPLATE: WorkshopTemplate = {
  id: "workshop-intro",
  label: "Workshop 入门",
  description: "How to generate a real KB-backed dashboard.",
  keywords: ["入门", "intro", "help"],
  build: (kb) => ({
    title: "从真实知识库生成页面",
    description:
      "使用左侧「新建」→「描述需求」，选定知识库并写下分析问题。服务端会读取文档列表、执行向量检索，并在配置了 LLM 预设时生成解读。",
    kb_id: kb.id,
    kb_name: kb.name,
    widgets: [
      {
        id: uid("w"),
        span: 6,
        widget: {
          type: "markdown",
          title: "怎么用",
          body:
            "- 点 **新建** ，切到 **描述需求**\n" +
            "- **必须选择知识库**（数据从该 KB 的 `/documents` 与 `/search` 拉取）\n" +
            '- 也可用 **HTML 小组件**（`type: "html"`）写可折叠的 `<details>`、表格和链接\n' +
            "- 也可以用 **选择模板** 生成本地演示数据页面\n\n" +
            "_之前版本里的营收示例已移除，避免与真实数据混淆。_",
        },
      },
      {
        id: uid("w"),
        span: 6,
        widget: {
          type: "html",
          title: "HTML 片段示例（原生可交互）",
          html:
            "<p>下面是 <strong>消毒后</strong> 的 HTML：<code>&lt;details&gt;</code> 可展开，" +
            '<a href="https://example.com" target="_blank" rel="noopener noreferrer">链接</a> 可点击。</p>' +
            "<details><summary>点我展开 / 收起</summary>" +
            "<p>事件与 <code>&lt;script&gt;</code> 会被剥离；样式建议用 <code>class</code>（受主题约束）。</p>" +
            "<table><thead><tr><th>列 A</th><th>列 B</th></tr></thead>" +
            "<tbody><tr><td>单元格</td><td>数据</td></tr></tbody></table></details>",
        },
      },
    ],
  }),
};

export const REVENUE_TEMPLATE: WorkshopTemplate = {
  id: "revenue-monthly",
  label: "过去一个月的收益",
  description: "Revenue dashboard. Daily totals, top customers, and category breakdown.",
  keywords: ["收益", "营收", "收入", "revenue", "sales", "earnings", "月度", "monthly", "income"],
  build: (kb) => {
    const rng = pseudo(7);
    const days = 30;
    const daily = Array.from({ length: days }, (_, i) => {
      const base = 4000 + Math.floor(rng() * 6000);
      const weekday = new Date(isoDaysAgo(days - 1 - i)).getDay();
      const weekend = weekday === 0 || weekday === 6 ? 0.6 : 1;
      return {
        label: isoDaysAgo(days - 1 - i).slice(5),
        value: Math.floor(base * weekend),
      };
    });
    const total = daily.reduce((s, d) => s + d.value, 0);
    const customers = [
      { id: uid("c"), name: "字节跳动", segment: "Enterprise", revenue: 38400, orders: 12 },
      { id: uid("c"), name: "美团", segment: "Enterprise", revenue: 27200, orders: 9 },
      { id: uid("c"), name: "知乎", segment: "Mid-market", revenue: 18600, orders: 7 },
      { id: uid("c"), name: "小红书", segment: "Mid-market", revenue: 14300, orders: 5 },
      { id: uid("c"), name: "Notion CN", segment: "SMB", revenue: 9100, orders: 4 },
    ];

    return {
      title: "过去一个月的收益",
      description: "按天聚合的营收，配 Top 5 客户和分类拆分。所有数据来自 KB 中的销售单据。",
      kb_id: kb.id,
      kb_name: kb.name,
      widgets: [
        {
          id: uid("w"),
          span: 2,
          widget: {
            type: "metric",
            title: "总营收 (30d)",
            value: `¥${total.toLocaleString()}`,
            delta: { value: 12.4, suffix: "%", label: "vs 上月" },
            hint: "应税前 GMV，按订单创建时间聚合",
          },
        },
        {
          id: uid("w"),
          span: 2,
          widget: {
            type: "metric",
            title: "活跃客户",
            value: `${118 + Math.floor(rng() * 12)}`,
            delta: { value: 8, suffix: " 家", label: "净新增" },
          },
        },
        {
          id: uid("w"),
          span: 2,
          widget: {
            type: "metric",
            title: "客单价",
            value: "¥3,720",
            delta: { value: -2.1, suffix: "%", label: "环比" },
            hint: "总营收 ÷ 订单数",
          },
        },
        {
          id: uid("w"),
          span: 6,
          widget: {
            type: "bar-chart",
            title: "每日营收",
            hint: "周末通常下滑约 40%",
            unit: "¥",
            data: daily,
          },
        },
        {
          id: uid("w"),
          span: 4,
          widget: {
            type: "table",
            title: "Top 5 客户",
            hint: "按营收倒序。可以直接编辑数据，看看交互。",
            columns: [
              { key: "name", label: "客户", kind: "text" },
              { key: "segment", label: "分层", kind: "tag" },
              { key: "revenue", label: "营收", kind: "number" },
              { key: "orders", label: "订单数", kind: "number" },
            ],
            rows: customers,
          },
        },
        {
          id: uid("w"),
          span: 2,
          widget: {
            type: "list",
            title: "按行业拆分",
            items: [
              { title: "互联网", meta: "¥58,400", tag: "47%" },
              { title: "AI / SaaS", meta: "¥31,800", tag: "26%" },
              { title: "教育", meta: "¥15,200", tag: "12%" },
              { title: "其他", meta: "¥18,100", tag: "15%" },
            ],
          },
        },
      ],
    };
  },
};

export const SUMMARY_TEMPLATE: WorkshopTemplate = {
  id: "recent-info-summary",
  label: "近期信息总结",
  description: "Recent activity in this knowledge base — new docs, threads, and AI insights.",
  keywords: [
    "总结",
    "信息",
    "动态",
    "近期",
    "summary",
    "recent",
    "digest",
    "weekly",
    "周报",
    "情报",
  ],
  build: (kb) => {
    const rng = pseudo(11);
    const trend = Array.from({ length: 14 }, (_, i) => ({
      x: isoDaysAgo(13 - i).slice(5),
      y: 3 + Math.floor(rng() * 9),
    }));
    return {
      title: "近期信息总结",
      description: "过去两周里 KB 里新增 / 改动的内容，以及自动总结的 takeaway。",
      kb_id: kb.id,
      kb_name: kb.name,
      widgets: [
        {
          id: uid("w"),
          span: 2,
          widget: {
            type: "metric",
            title: "新增文档",
            value: "47",
            delta: { value: 12, suffix: " 篇", label: "近 14 天" },
          },
        },
        {
          id: uid("w"),
          span: 2,
          widget: {
            type: "metric",
            title: "新建会话",
            value: "238",
            delta: { value: 18.6, suffix: "%", label: "环比" },
            hint: "Agent / 用户 在该 KB 上发起的对话",
          },
        },
        {
          id: uid("w"),
          span: 2,
          widget: {
            type: "metric",
            title: "知识覆盖率",
            value: "82%",
            delta: { value: 4, suffix: "pp", label: "vs 上周" },
            hint: "命中文档的提问比例",
          },
        },
        {
          id: uid("w"),
          span: 6,
          widget: {
            type: "line-chart",
            title: "每日新增文档",
            hint: "周一通常是上传高峰",
            unit: "篇",
            data: trend,
          },
        },
        {
          id: uid("w"),
          span: 4,
          widget: {
            type: "list",
            title: "本周 takeaway",
            hint: "由 LLM 总结，每条都引用了具体文档段落。",
            items: [
              {
                title: "OCR 流水线对扫描件失败率上升",
                body: "Unstructured 1.6 在 PDF 双栏排版下偶发列错位；建议把 strategy 切到 hi_res。",
                tag: "bug",
                meta: "5 篇文档",
              },
              {
                title: "新接入 3 个 Feishu 群组",
                body: "需要重新跑一次 Skills 注册脚本，否则 /channels 里的 binding 状态不会刷新。",
                tag: "ops",
                meta: "2 篇 runbook",
              },
              {
                title: "客户更频繁地问 SSO 集成",
                body: "近 7 天里 OIDC 相关问题占比 14%，可考虑写一篇 SSO Quickstart。",
                tag: "content",
                meta: "11 个会话",
              },
              {
                title: "Aurora IAM token 偶发过期",
                body: "下午 3 点附近多次超时；建议把 cache TTL 从 14m 调到 10m。",
                tag: "infra",
                meta: "Slack #ops",
              },
            ],
          },
        },
        {
          id: uid("w"),
          span: 2,
          widget: {
            type: "markdown",
            title: "执行摘要",
            body:
              "**🎯 这周重点**\n\n" +
              "- OCR 流水线需要排查\n" +
              "- 客户在问 SSO，可补一篇文档\n" +
              "- DB IAM token 偶发过期\n\n" +
              "_自动生成，可以改写后保存_",
          },
        },
      ],
    };
  },
};

export const GROWTH_TEMPLATE: WorkshopTemplate = {
  id: "growth-funnel",
  label: "增长漏斗",
  description: "Top-of-funnel through activated users, with conversion at each step.",
  keywords: ["增长", "growth", "funnel", "漏斗", "conversion", "activation"],
  build: (kb) => {
    return {
      title: "增长漏斗",
      description: "从访问到激活的转化漏斗，按周聚合。",
      kb_id: kb.id,
      kb_name: kb.name,
      widgets: [
        {
          id: uid("w"),
          span: 3,
          widget: {
            type: "metric",
            title: "周访问 UV",
            value: "12,840",
            delta: { value: 6.2, suffix: "%", label: "WoW" },
          },
        },
        {
          id: uid("w"),
          span: 3,
          widget: {
            type: "metric",
            title: "激活率",
            value: "21.3%",
            delta: { value: 1.4, suffix: "pp", label: "WoW" },
          },
        },
        {
          id: uid("w"),
          span: 6,
          widget: {
            type: "bar-chart",
            title: "漏斗各阶段",
            data: [
              { label: "访问", value: 12840 },
              { label: "注册", value: 3420 },
              { label: "首次发消息", value: 2180 },
              { label: "持续 3 天", value: 1480 },
              { label: "付费", value: 432 },
            ],
          },
        },
        {
          id: uid("w"),
          span: 6,
          widget: {
            type: "markdown",
            body:
              "**漏斗读法**\n\n" +
              "- 访问 → 注册转化 26.6%（行业基线 ~22%）\n" +
              "- 注册 → 首次发消息 63.7%（健康）\n" +
              "- 持续 3 天 → 付费 29%，是当前最薄弱的一步",
          },
        },
      ],
    };
  },
};

export const BLANK_TEMPLATE: WorkshopTemplate = {
  id: "blank",
  label: "空白页面",
  description: "Just a title + a markdown card you can edit.",
  keywords: [],
  build: (kb) => ({
    title: "新页面",
    description: "",
    kb_id: kb.id,
    kb_name: kb.name,
    widgets: [
      {
        id: uid("w"),
        span: 6,
        widget: {
          type: "markdown",
          title: "开始",
          body: "这是一个空白页面。\n\n后续版本里你可以在这里加 widgets（指标卡 / 表格 / 图表 / 列表）。\n\n现在用这个页面记笔记也行。",
        },
      },
    ],
  }),
};

export const ALL_TEMPLATES: WorkshopTemplate[] = [
  INTRO_TEMPLATE,
  REVENUE_TEMPLATE,
  SUMMARY_TEMPLATE,
  GROWTH_TEMPLATE,
  BLANK_TEMPLATE,
];

/** Pick the best-matching template for a free-text prompt; falls back
 *  to the blank starter when nothing trips a keyword. */
export function pickTemplate(prompt: string): WorkshopTemplate {
  const lower = prompt.trim().toLowerCase();
  if (!lower) return BLANK_TEMPLATE;
  let best: { tpl: WorkshopTemplate; score: number } = {
    tpl: BLANK_TEMPLATE,
    score: 0,
  };
  for (const tpl of ALL_TEMPLATES) {
    let score = 0;
    for (const kw of tpl.keywords) {
      if (lower.includes(kw.toLowerCase())) score += 1;
    }
    if (score > best.score) best = { tpl, score };
  }
  return best.tpl;
}
